import "./styles.scss";

// Chamfered top-right corner with a double (red + white) line. All defaults
// below are measured pixel-for-pixel from the reference asset
// (results_border.webp, 838x345) rather than guessed:
//  - line thickness ~2px, ~10px between line starts (~8px visible gap)
//  - chamfer ~28px x ~24px
//  - top edge: each line is solid near both ends and invisible through the
//    middle (a gap in the line itself, not a single smooth fade)
//  - verticals: red is solid then fades out over a FIXED pixel span (not
//    proportional to container height); white stays solid until deep into
//    the container and only fades out right at the bottom edge
// Tune via props — each one maps straight to a CSS custom property consumed
// by styles.scss, so no JS math/layout logic lives here. Pixel-based
// defaults were measured against an 838px-wide reference; rescale
// chamfer/lineThickness/gap/redDrop if your container is a very different
// size.
const CornerFrame = ({
  children,
  chamfer = 28,
  lineThickness = 3,
  gap = 10,
  redColor = "#ec0037",
  whiteColor = "#ffffff",
  // Vertical (left/right) fades.
  // Red: solid for redDropSolid px, then fades to transparent by redDrop px
  // (a fixed pixel distance from the top corner, independent of container
  // height — kept short/static by design, not scaled from the reference
  // asset's own height).
  redDrop = 250,
  redDropSolid = 42,
  // White: solid until whiteFadeStart% of the container height, then fades
  // to transparent by whiteFadeEnd% (~the bottom edge) — this one *is*
  // proportional, since it's meant to always reach the container's bottom.
  whiteFadeStart = 78,
  whiteFadeEnd = 99,
  // Top-edge segmented fade, as % of the top line's own length (0 = left
  // end, 100 = the corner). Solid from 0 to fadeOutStart, transparent from
  // fadeOutEnd to fadeInStart, solid again from fadeInEnd to 100.
  redTopFadeOutStart = 15,
  redTopFadeOutEnd = 26,
  redTopFadeInStart = 78,
  redTopFadeInEnd = 88,
  whiteTopFadeOutStart = 23,
  whiteTopFadeOutEnd = 32,
  whiteTopFadeInStart = 72,
  whiteTopFadeInEnd = 80,
  // The white top line stops this many px short of the corner compared to
  // the red one, instead of running the same length.
  whiteTopShrink = 16,
  // White starts this many px later than red along its own running
  // direction too — shifted right on the top edge, shifted down on both
  // verticals — instead of starting flush at the same point as red.
  whiteInsetStart = 13,
  whiteBottomInsect = 100,
  chamferExtra = 7,
  rightWhiteEnd=2,
  whiteExtraTop=1,
  rightExtraBottom = 18,
  className = "",
  style,
}) => {
  return (
    <div
      className={`corner-frame ${className}`}
      style={{
        "--corner-frame-chamfer": `${chamfer}px`,
        "--corner-frame-line": `${lineThickness}px`,
        "--corner-frame-gap": `${gap}px`,
        "--corner-frame-red": redColor,
        "--corner-frame-white": whiteColor,
        "--corner-frame-red-drop": `${redDrop}px`,
        "--corner-frame-red-drop-solid": `${redDropSolid}px`,
        "--corner-frame-white-fade-start": `${whiteFadeStart}%`,
        "--corner-frame-white-fade-end": `${whiteFadeEnd}%`,
        "--corner-frame-red-top-fade-out-start": `${redTopFadeOutStart}%`,
        "--corner-frame-red-top-fade-out-end": `${redTopFadeOutEnd}%`,
        "--corner-frame-red-top-fade-in-start": `${redTopFadeInStart}%`,
        "--corner-frame-red-top-fade-in-end": `${redTopFadeInEnd}%`,
        "--corner-frame-white-top-fade-out-start": `${whiteTopFadeOutStart}%`,
        "--corner-frame-white-top-fade-out-end": `${whiteTopFadeOutEnd}%`,
        "--corner-frame-white-top-fade-in-start": `${whiteTopFadeInStart}%`,
        "--corner-frame-white-top-fade-in-end": `${whiteTopFadeInEnd}%`,
        "--corner-frame-white-top-shrink": `${whiteTopShrink}px`,
        "--corner-frame-white-inset-start": `${whiteInsetStart}px`,
        "--corner-frame-white-insect-bottom": `${whiteBottomInsect}px`,
        "--corner-frame-chamfer-extra": `${chamferExtra}px`,
        "--corner-frame-right-white-end": `${rightWhiteEnd}px`,
        "--corner-frame-white-extra-top": `${whiteExtraTop}px`,
        "--corner-frame-right-extra-bottom": `${rightExtraBottom}px`,
        ...style,
      }}
    >
      <div className="corner-frame__edge corner-frame__top corner-frame__red" />
      <div className="corner-frame__edge corner-frame__top corner-frame__white" />
      <div className="corner-frame__edge corner-frame__diag corner-frame__red" />
      <div className="corner-frame__edge corner-frame__diag corner-frame__white" />
      <div className="corner-frame__edge corner-frame__vert-left corner-frame__red-fade" />
      <div className="corner-frame__edge corner-frame__vert-left corner-frame__white-fade" />
      <div className="corner-frame__edge corner-frame__vert-right corner-frame__red-fade" />
      <div className="corner-frame__edge corner-frame__vert-right corner-frame__white-fade" />

      <div className="corner-frame__content">{children}</div>
    </div>
  );
};

export default CornerFrame;
